import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { SlideAdminNotesCell } from '@/components/admin/SlideAdminNotes/SlideAdminNotes';
import { NewestStatus } from '@/components/admin/SlideAdminNotes/SlideStatusPill';
import cn from '@/utils/cn';
import type { CopiedSlide } from './CopySlideDialog';
import SlideActions from './SlideActions';
import { editUrl } from './slideUrls';
import type { SlideRow, SlideTableContext } from './types';

interface ColumnOptions {
  context: SlideTableContext;
  /** Every row shows all its notes instead of the newest one's preview. */
  notesExpanded: boolean;
  onCopied: (source: SlideRow, copy: CopiedSlide) => void;
  onDeleted: (slide: SlideRow) => void;
  onToggled: (slide: SlideRow) => void;
  stafferId: number;
  statuses: string[];
}

const columnHelper = createColumnHelper<SlideRow>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline';

const TitleCell = ({ context, slide }: { context: SlideTableContext; slide: SlideRow }) => (
  <a className={cn(linkClass, slide.hidden && 'tw-opacity-60')} href={editUrl(slide, context)}>
    {slide.title}
    {slide.hidden && <span className="tw-ml-1.5 tw-text-xs tw-text-spa-tertiary">(hidden)</span>}
  </a>
);

// Every column is unsortable: the rows are drag-reorderable, and the table asks for the data order
// to be the display order when reorder is on.
const slideColumns = ({
  context, notesExpanded, onCopied, onDeleted, onToggled, stafferId, statuses,
}: ColumnOptions): ColumnDef<SlideRow, unknown>[] => [
  columnHelper.display({
    cell: info => <span className="tw-tabular-nums tw-text-spa-tertiary">{info.row.index + 1}</span>,
    enableSorting: false,
    header: '#',
    id: 'position',
    size: 40,
  }),
  columnHelper.accessor('title', {
    cell: info => <TitleCell context={context} slide={info.row.original} />,
    enableSorting: false,
    header: 'Title',
    size: 200,
  }),
  columnHelper.accessor('id', {
    cell: info => <NewestStatus initialNotes={info.row.original.notes} slideId={info.getValue()} />,
    enableSorting: false,
    header: 'Assignee',
    id: 'status',
    size: 90,
  }),
  columnHelper.display({
    cell: info => (
      <SlideAdminNotesCell
        expanded={notesExpanded}
        initialNotes={info.row.original.notes}
        slideId={info.row.original.id}
        slidePosition={info.row.index + 1}
        slideTitle={info.row.original.title}
        stafferId={stafferId}
        statuses={statuses}
      />
    ),
    enableSorting: false,
    header: 'Notes',
    id: 'notes',
    meta: { width: 'auto' },
  }),
  columnHelper.display({
    cell: info => (
      <SlideActions
        context={context}
        slide={info.row.original}
        onCopied={onCopied}
        onDeleted={onDeleted}
        onToggled={onToggled}
      />
    ),
    enableSorting: false,
    header: 'Actions',
    id: 'actions',
    meta: { width: 1 },
  }),
];

export default slideColumns;
